import {
  compose,
  prop,
  find,
  uniqWith,
  equals,
  map,
  flatten,
  pathEq,
} from 'ramda';
import { NOT_FOUND } from 'redux-first-router';

import { isServer, getPlatform } from '../utils';
import { getComponentMap } from '../component-mappings';
import { entryLoader } from '../contentful/loaders';
import { setEntries } from '../../store/content/actions';
import { pageLoading, pageLoaded } from '../../store/page/actions';
import {
  selectFields,
  selectFieldsByKey,
  selectContentType,
} from '../contentful/selectors.mjs';

import { generateStaticRoutes } from './statics';

/**
 * Contains metadata used to create a route. Must have either an entryId for a
 * managed page or a component for a static page.
 *
 * @typedef {object} Page
 * @property {string} path - The URL path the route is defined for.
 * @property {string} [entryId] - For managed pages this identifies the ID of
 *   the content to request.
 * @property {string} [component] - For static pages this is used to identify
 *   the base component to render.
 */

/**
 * The full route object.
 *
 * @typedef {object} Route
 * @property {string} path - The URL path the route is defined for.
 * @property {Function} thunk - Can be an async function.
 * @property {string} [entryId] - For managed pages this identifies the ID of
 *   the content to request.
 * @property {string} [component] - For static pages this is used to identify
 *   the base component to render.
 */

/**
 * A map of actions to Route objects.
 *
 * @typedef {{<string, Route>}} RouteList
 */

const awaitContentThunk = (page) => async (dispatch, getState) => {
  const { entryId } = page;
  const state = getState();
  const platform = getPlatform();
  dispatch(pageLoading());

  const entries = await entryLoader.loadMany([entryId]);
  const pageFields = compose(
    selectFields,
    find(pathEq(['sys', 'id'], entryId))
  )(entries);

  const getGuards = compose(
    uniqWith(equals),
    flatten,
    map((row) => {
      const guards = getComponentMap()[selectContentType(row)]?.guards || [];
      if (row?.fields?.variants?.length) {
        return [getGuards(row.fields.variants), guards];
      }
      return guards;
    })
  );

  const guards = getGuards(pageFields.rows);

  for (const guard of guards) {
    if (guard(dispatch, getState)) return;
  }

  const appleAppId = compose(
    prop('appleAppId'),
    selectFieldsByKey('metadata')
  )(pageFields);

  // If page content has an appleAppId in the metadata then a full page load
  // needs to occur so the iOS app banner is displayed properly. This check only
  // needs to occur on iOS devices in the client (not server side).
  if (!isServer && appleAppId && platform == 'ios') {
    window.location.href = state.location.pathname;
    return;
  }

  compose(dispatch, setEntries)(entries);
  dispatch(pageLoaded());
};

function matchAction(page) {
  const { entryId } = page;

  switch (page.path) {
    case '/not-found':
      return NOT_FOUND;
    default:
      return entryId;
  }
}

/**
 * Builds the list of routes defined statically and pulled dynamically from
 * another source as provided the `pages` argument.
 *
 * @param {Page[]} pages - Pages to add to the list of routes.
 * @param {object} options
 * @param {object} options.graphqlClient - Instance of the client to run queries
 *   on the GraphQL server.
 * @param {object} options.ctx
 * @returns {RouteList} The array of routes.
 */
const createRoutesMap = (pages = [], { ctx, graphqlClient } = {}) =>
  pages.reduce(
    (map, page) => {
      const action = matchAction(page);
      const thunk = awaitContentThunk(page);

      map[action] = { thunk, ...page, dynamic: true };

      return map;
    },
    { ...generateStaticRoutes({ ctx, graphqlClient }) }
  );

export default createRoutesMap;
